import React, { useState, useEffect, useLayoutEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation, Navigate, useNavigate } from 'react-router-dom';
import Header from './components/Header/Header';
import Footer from './components/Footer/Footer';
import Home from './pages/Home';
import Services from './pages/Services';
import Projects from './pages/Projects';
import Companies from './pages/Companies';
import Course from './pages/Course';
import Education from './pages/Education';
import Contact from './pages/Contact';
import Login from './pages/Login';
import RealEstateDashboard from './pages/RealEstateDashboard';
import { AuthSession } from './types';
import ScrollProgress from './components/ScrollProgress';

const ScrollToTop: React.FC = () => {
  const location = useLocation();

  useLayoutEffect(() => {
    window.scrollTo({
      top: 0,
      behavior: 'instant' as ScrollBehavior
    });
  }, [location.pathname]);

  return null;
};

interface ProtectedRouteProps {
  isAuthenticated: boolean;
  permissions: string[];
  requiredPermission: string;
  children: JSX.Element;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  isAuthenticated,
  permissions,
  requiredPermission,
  children
}) => {
  const location = useLocation();

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location.pathname }} replace />;
  }

  if (!permissions.includes(requiredPermission)) {
    return <Navigate to="/" replace />;
  }

  return children;
};

const AppRoutes: React.FC<{
  session: AuthSession | null;
  onLogin: (session: AuthSession) => void;
}> = ({ session, onLogin }) => {
  const navigate = useNavigate();
  const location = useLocation();

  const handleLogin = (authSession: AuthSession) => {
    onLogin(authSession);
    const from = (location.state as { from?: string } | null)?.from || '/imoveis';
    navigate(from, { replace: true });
  };

  return (
    <Routes>
      <Route path="/" element={<Home />} />
      <Route path="/services" element={<Services />} />
      <Route path="/projects" element={<Projects />} />
      <Route path="/companies" element={<Companies />} />
      <Route path="/course" element={<Course />} />
      <Route path="/education" element={<Education />} />
      <Route path="/contact" element={<Contact />} />
      <Route path="/login" element={<Login onLogin={handleLogin} />} />
      <Route
        path="/imoveis"
        element={
          <ProtectedRoute
            isAuthenticated={Boolean(session?.token)}
            permissions={session?.user.permissions || []}
            requiredPermission="properties:read"
          >
            <RealEstateDashboard session={session} />
          </ProtectedRoute>
        }
      />
    </Routes>
  );
};

const App: React.FC = () => {
  const [isDarkMode, setIsDarkMode] = useState(true);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [session, setSession] = useState<AuthSession | null>(() => {
    const rawSession = sessionStorage.getItem('profile-dev-session');

    if (!rawSession) return null;

    try {
      return JSON.parse(rawSession);
    } catch {
      return null;
    }
  });

  useEffect(() => {
    if (isDarkMode) {
      document.body.classList.add('dark-mode');
    } else {
      document.body.classList.remove('dark-mode');
    }
  }, [isDarkMode]);

  const toggleTheme = () => {
    setIsDarkMode(!isDarkMode);
  };

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  const closeMenu = () => {
    setIsMenuOpen(false);
  };

  const handleLogin = (authSession: AuthSession) => {
    sessionStorage.setItem('profile-dev-session', JSON.stringify(authSession));
    setSession(authSession);
  };

  const handleLogout = () => {
    sessionStorage.removeItem('profile-dev-session');
    setSession(null);
  };

  return (
    <Router>
      <ScrollToTop />
      <ScrollProgress />
      <div className="portfolio-app">
        <a className="skip-link" href="#main-content">Pular para o conteúdo</a>
        <Header 
          isMenuOpen={isMenuOpen} 
          toggleMenu={toggleMenu}
          closeMenu={closeMenu}
          isDarkMode={isDarkMode}
          toggleTheme={toggleTheme}
          isAuthenticated={Boolean(session?.token)}
          userName={session?.user.displayName || session?.user.username || ''}
          onLogout={handleLogout}
        />
        <main className="main-content" id="main-content" tabIndex={-1}>
          <AppRoutes session={session} onLogin={handleLogin} />
        </main>
        <Footer />
      </div>
    </Router>
  );
};

export default App;
